/** Data hooks for Phase 7: service tickets, installed base, AMC contracts, the AI copilot,
 *  the report builder and the forecast. */
import { useQuery } from "@tanstack/react-query";

import { api, qs } from "./client";
import type { Ref, UserRef } from "./types";

// ---- installed base and tickets ----

export interface Coverage { kind: "warranty" | "amc" | "chargeable"; label: string; until: string | null; contract: Ref | null }
export interface Asset {
  id: string; name: string; serial_number: string; account: Ref; product: Ref | null; order_id: string | null;
  installed_on: string | null; warranty_until: string | null; location: string | null; status: "active" | "retired"; notes: string | null;
  coverage: Coverage | null; open_tickets: number; created_at: string;
}
export type TicketStatus = "open" | "scheduled" | "in_progress" | "waiting_parts" | "resolved" | "closed";
export type Priority = "low" | "normal" | "high" | "urgent";
export type TicketKind = "breakdown" | "preventive" | "installation" | "complaint" | "amc_visit";
export interface TicketSummary {
  id: string; number: string; kind: TicketKind; priority: Priority; status: TicketStatus; subject: string; account: Ref;
  asset: Ref | null; assignee: UserRef | null; coverage: Coverage["kind"]; due_at: string | null; scheduled_for: string | null;
  resolved_at: string | null; overdue: boolean; created_at: string;
}
export interface Part { product_id: string | null; sku: string | null; description: string; quantity: number }
export interface Ticket extends TicketSummary {
  description: string | null; contact: { id: string; full_name: string } | null; serial_number: string | null; contract: Ref | null;
  conversation_id: string | null; created_by: UserRef | null; source: string; claim_status: "claimed" | "approved" | "rejected" | null;
  resolution: string | null; parts: Part[]; parts_quote_id: string | null; updated_at: string;
}
export interface TicketCounts { open: number; overdue: number; mine: number }

export interface Contract {
  id: string; number: string; account: Ref; owner: UserRef | null; starts_on: string; ends_on: string; value: number; currency: string;
  visits_per_year: number; status: "active" | "expired" | "renewed" | "cancelled"; terms: string | null; days_left: number; renewal_due: boolean;
  assets: Ref[]; visits: TicketSummary[]; revenue: { month: string; amount: number }[]; renewal_quote_id: string | null;
  renewed_from: string | null; renewed_to: string | null; created_at: string;
}

export const useAssets = (filters: { account_id?: string; q?: string; coverage?: string }, enabled = true) =>
  useQuery({ queryKey: ["assets", filters], queryFn: () => api<Asset[]>("GET", `/assets${qs(filters)}`), enabled });
export const useTickets = (filters: { status?: string; mine?: boolean; account_id?: string; asset_id?: string; overdue?: boolean }) =>
  useQuery({ queryKey: ["tickets", filters], queryFn: () => api<TicketSummary[]>("GET", `/tickets${qs(filters)}`) });
export const useTicket = (id: string | undefined) =>
  useQuery({ queryKey: ["ticket", id], queryFn: () => api<Ticket>("GET", `/tickets/${id}`), enabled: !!id });
export const useTicketCounts = (enabled = true) =>
  useQuery({ queryKey: ["ticket-counts"], queryFn: () => api<TicketCounts>("GET", "/tickets/counts"), enabled, refetchInterval: 60_000 });
export const useContracts = (filters: { status?: string; account_id?: string; renewal_due?: boolean }) =>
  useQuery({ queryKey: ["amc", filters], queryFn: () => api<Contract[]>("GET", `/amc${qs(filters)}`) });
export const useContract = (id: string | undefined) =>
  useQuery({ queryKey: ["amc-contract", id], queryFn: () => api<Contract>("GET", `/amc/${id}`), enabled: !!id });

// ---- AI ----

export interface AiStatus { available: boolean; enabled: boolean; configured: boolean; calls_this_month: number }
export interface Source { type: string; id: string; label: string }
export interface Summary { summary: string; next_steps: string[]; sentiment: string; confidence: "high" | "medium" | "low"; sources: Source[]; generated_at: string }
export interface Draft { subject: string | null; body: string; sources: Source[] }
export interface Score { score: number; band: "hot" | "warm" | "cold"; reasons: string[]; scored_at: string }

export const useAiStatus = () => useQuery({ queryKey: ["ai-status"], queryFn: () => api<AiStatus>("GET", "/ai"), staleTime: 60_000 });

// ---- report builder and forecast ----

export interface CatalogObject {
  key: string; label: string; dates: { key: string; label: string }[];
  measures: { key: string; label: string; type: "number" | "money" }[]; dims: { key: string; label: string }[];
}
export interface ReportFilter { field: string; op: "equals" | "not_equals" | "in" | "contains"; value: unknown }
export interface ReportSpec {
  object: string; measure: string; group_by: string[]; filters: ReportFilter[]; date_field?: string | null; period: string;
  date_from?: string | null; date_to?: string | null; chart: "bar" | "column" | "line" | "table" | "number"; sort?: "value" | "label"; limit?: number;
}
export interface ReportResult {
  columns: { key: string; label: string; type: "text" | "number" | "money" }[]; rows: (string | number | null)[][];
  total: number | null; date_from: string | null; date_to: string | null; truncated: boolean;
}
export interface SavedReport { id: string; name: string; spec: ReportSpec; shared: boolean; owner: UserRef | null; mine: boolean; updated_at: string }
export interface Ask { question: string; explanation: string; spec: ReportSpec; result: ReportResult }
export interface ForecastCell { target: number; won: number; commit: number; best_case: number; pipeline: number; weighted: number; deals: number }
export interface Forecast {
  currency: string; months: string[]; reps: { user_id: string; name: string; months: Record<string, ForecastCell>; total: ForecastCell }[];
  totals: Record<string, ForecastCell>; total: ForecastCell; no_close_date: number;
}

export const useCatalog = () => useQuery({ queryKey: ["report-catalog"], queryFn: () => api<CatalogObject[]>("GET", "/reports/catalog"), staleTime: Infinity });
export const useSavedReports = () => useQuery({ queryKey: ["saved-reports"], queryFn: () => api<SavedReport[]>("GET", "/reports/saved") });
export const useReport = (spec: ReportSpec | null) =>
  useQuery({ queryKey: ["report-run", spec], queryFn: () => api<ReportResult>("POST", "/reports/run", spec!), enabled: !!spec });
export const useForecast = (filters: { start?: string; months: number; owner_id?: string }) =>
  useQuery({ queryKey: ["forecast", filters], queryFn: () => api<Forecast>("GET", `/reports/forecast${qs(filters)}`) });
